/**
 * EU + selected non-EU VAT rates registry with temporal validity.
 * Data source: EC TAXUD, Tax Foundation. Last verified: 2026-02-16.
 *
 * @module vat-rates
 */

import { Decimal } from 'decimal.js';
import { type Temporal, nowPlainDate } from '@insio/dates';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Simple validation result used by VAT rate validation functions. */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export const VatRateCategory = {
  STANDARD: 'STANDARD',
  REDUCED: 'REDUCED',
  REDUCED_2: 'REDUCED_2',
  SUPER_REDUCED: 'SUPER_REDUCED',
  PARKING: 'PARKING',
  ZERO: 'ZERO'
} as const;

export type VatRateCategory = (typeof VatRateCategory)[keyof typeof VatRateCategory];

export interface VatRateEntry {
  category: VatRateCategory;
  /** Rate as percentage (e.g. Decimal(21) for 21%, Decimal('2.1') for 2.1%) */
  rate: Decimal;
  /** ISO date (YYYY-MM-DD) when this rate became effective */
  validFrom: string;
  /** ISO date (YYYY-MM-DD) when superseded, or null if current */
  validTo: string | null;
}

export interface CountryVatInfo {
  countryName: string;
  isEuMember: boolean;
  rates: readonly VatRateEntry[];
}

export interface VatRateLookupResult {
  countryCode: string;
  countryName: string;
  rates: VatRateEntry[];
}

// ---------------------------------------------------------------------------
// Registry (deep-frozen to prevent accidental mutation of shared state)
// ---------------------------------------------------------------------------

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object') {
    Object.freeze(value);
    for (const v of Object.values(value as object)) deepFreeze(v);
  }
  return value;
}

export const VAT_RATES: Record<string, CountryVatInfo> = deepFreeze({
  AT: {
    countryName: 'Austria',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(20), validFrom: '2016-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(10), validFrom: '2016-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(13), validFrom: '2016-01-01', validTo: null },
      { category: 'PARKING', rate: new Decimal(13), validFrom: '2016-01-01', validTo: null }
    ]
  },
  BE: {
    countryName: 'Belgium',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(21), validFrom: '1996-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(6), validFrom: '1996-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(12), validFrom: '1996-01-01', validTo: null },
      { category: 'PARKING', rate: new Decimal(12), validFrom: '1996-01-01', validTo: null }
    ]
  },
  BG: {
    countryName: 'Bulgaria',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(20), validFrom: '2007-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(9), validFrom: '2007-01-01', validTo: null }
    ]
  },
  CY: {
    countryName: 'Cyprus',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(19), validFrom: '2014-01-13', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2014-01-13', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(9), validFrom: '2014-01-13', validTo: null }
    ]
  },
  CZ: {
    countryName: 'Czech Republic',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(21), validFrom: '2013-01-01', validTo: null },
      {
        category: 'REDUCED',
        rate: new Decimal(15),
        validFrom: '2013-01-01',
        validTo: '2023-12-31'
      },
      {
        category: 'REDUCED_2',
        rate: new Decimal(10),
        validFrom: '2015-01-01',
        validTo: '2023-12-31'
      },
      { category: 'REDUCED', rate: new Decimal(12), validFrom: '2024-01-01', validTo: null }
    ]
  },
  DE: {
    countryName: 'Germany',
    isEuMember: true,
    rates: [
      {
        category: 'STANDARD',
        rate: new Decimal(19),
        validFrom: '2007-01-01',
        validTo: '2020-06-30'
      },
      { category: 'REDUCED', rate: new Decimal(7), validFrom: '2007-01-01', validTo: '2020-06-30' },
      {
        category: 'STANDARD',
        rate: new Decimal(16),
        validFrom: '2020-07-01',
        validTo: '2020-12-31'
      },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2020-07-01', validTo: '2020-12-31' },
      { category: 'STANDARD', rate: new Decimal(19), validFrom: '2021-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(7), validFrom: '2021-01-01', validTo: null }
    ]
  },
  DK: {
    countryName: 'Denmark',
    isEuMember: true,
    rates: [{ category: 'STANDARD', rate: new Decimal(25), validFrom: '1992-01-01', validTo: null }]
  },
  EE: {
    countryName: 'Estonia',
    isEuMember: true,
    rates: [
      {
        category: 'STANDARD',
        rate: new Decimal(22),
        validFrom: '2024-01-01',
        validTo: '2025-06-30'
      },
      { category: 'REDUCED', rate: new Decimal(9), validFrom: '2024-01-01', validTo: '2025-06-30' },
      {
        category: 'REDUCED_2',
        rate: new Decimal(13),
        validFrom: '2024-01-01',
        validTo: '2025-06-30'
      },
      { category: 'STANDARD', rate: new Decimal(24), validFrom: '2025-07-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(9), validFrom: '2025-07-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(13), validFrom: '2025-07-01', validTo: null }
    ]
  },
  EL: {
    countryName: 'Greece',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(24), validFrom: '2016-06-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(6), validFrom: '2016-06-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(13), validFrom: '2016-06-01', validTo: null }
    ]
  },
  ES: {
    countryName: 'Spain',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(21), validFrom: '2012-09-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(10), validFrom: '2012-09-01', validTo: null },
      { category: 'SUPER_REDUCED', rate: new Decimal(4), validFrom: '2012-09-01', validTo: null }
    ]
  },
  FI: {
    countryName: 'Finland',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal('25.5'), validFrom: '2024-09-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(10), validFrom: '2024-09-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal('13.5'), validFrom: '2025-01-01', validTo: null },
      {
        category: 'STANDARD',
        rate: new Decimal(24),
        validFrom: '2013-01-01',
        validTo: '2024-08-31'
      },
      {
        category: 'REDUCED',
        rate: new Decimal(10),
        validFrom: '2013-01-01',
        validTo: '2024-08-31'
      },
      {
        category: 'REDUCED_2',
        rate: new Decimal(14),
        validFrom: '2013-01-01',
        validTo: '2024-08-31'
      }
    ]
  },
  FR: {
    countryName: 'France',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(20), validFrom: '2014-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(10), validFrom: '2014-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal('5.5'), validFrom: '2014-01-01', validTo: null },
      {
        category: 'SUPER_REDUCED',
        rate: new Decimal('2.1'),
        validFrom: '2014-01-01',
        validTo: null
      }
    ]
  },
  HR: {
    countryName: 'Croatia',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(25), validFrom: '2013-07-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2013-07-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(13), validFrom: '2013-07-01', validTo: null }
    ]
  },
  HU: {
    countryName: 'Hungary',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(27), validFrom: '2012-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2012-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(18), validFrom: '2012-01-01', validTo: null }
    ]
  },
  IE: {
    countryName: 'Ireland',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(23), validFrom: '2012-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(9), validFrom: '2012-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal('13.5'), validFrom: '2012-01-01', validTo: null },
      {
        category: 'SUPER_REDUCED',
        rate: new Decimal('4.8'),
        validFrom: '2012-01-01',
        validTo: null
      },
      { category: 'PARKING', rate: new Decimal('13.5'), validFrom: '2012-01-01', validTo: null }
    ]
  },
  IT: {
    countryName: 'Italy',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(22), validFrom: '2013-10-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2013-10-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(10), validFrom: '2013-10-01', validTo: null },
      { category: 'SUPER_REDUCED', rate: new Decimal(4), validFrom: '2013-10-01', validTo: null }
    ]
  },
  LT: {
    countryName: 'Lithuania',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(21), validFrom: '2009-09-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2009-09-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(9), validFrom: '2009-09-01', validTo: null }
    ]
  },
  LU: {
    countryName: 'Luxembourg',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(17), validFrom: '2024-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(8), validFrom: '2024-01-01', validTo: null },
      { category: 'SUPER_REDUCED', rate: new Decimal(3), validFrom: '2024-01-01', validTo: null },
      { category: 'PARKING', rate: new Decimal(14), validFrom: '2024-01-01', validTo: null }
    ]
  },
  LV: {
    countryName: 'Latvia',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(21), validFrom: '2012-07-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2012-07-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(12), validFrom: '2012-07-01', validTo: null }
    ]
  },
  MT: {
    countryName: 'Malta',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(18), validFrom: '2004-05-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2004-05-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(7), validFrom: '2004-05-01', validTo: null },
      { category: 'PARKING', rate: new Decimal(12), validFrom: '2004-05-01', validTo: null }
    ]
  },
  NL: {
    countryName: 'Netherlands',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(21), validFrom: '2012-10-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(9), validFrom: '2019-01-01', validTo: null }
    ]
  },
  PL: {
    countryName: 'Poland',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(23), validFrom: '2011-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2011-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(8), validFrom: '2011-01-01', validTo: null }
    ]
  },
  PT: {
    countryName: 'Portugal',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(23), validFrom: '2011-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(6), validFrom: '2011-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(13), validFrom: '2011-01-01', validTo: null },
      { category: 'PARKING', rate: new Decimal(13), validFrom: '2011-01-01', validTo: null }
    ]
  },
  RO: {
    countryName: 'Romania',
    isEuMember: true,
    rates: [
      {
        category: 'STANDARD',
        rate: new Decimal(19),
        validFrom: '2017-01-01',
        validTo: '2025-07-31'
      },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2017-01-01', validTo: '2025-07-31' },
      {
        category: 'REDUCED_2',
        rate: new Decimal(9),
        validFrom: '2017-01-01',
        validTo: '2025-07-31'
      },
      { category: 'STANDARD', rate: new Decimal(21), validFrom: '2025-08-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(11), validFrom: '2025-08-01', validTo: null }
    ]
  },
  SE: {
    countryName: 'Sweden',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(25), validFrom: '1995-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(6), validFrom: '1995-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(12), validFrom: '1995-01-01', validTo: null }
    ]
  },
  SI: {
    countryName: 'Slovenia',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(22), validFrom: '2013-07-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2013-07-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal('9.5'), validFrom: '2013-07-01', validTo: null }
    ]
  },
  SK: {
    countryName: 'Slovakia',
    isEuMember: true,
    rates: [
      { category: 'STANDARD', rate: new Decimal(23), validFrom: '2025-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2025-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(19), validFrom: '2025-01-01', validTo: null },
      {
        category: 'STANDARD',
        rate: new Decimal(20),
        validFrom: '2011-01-01',
        validTo: '2024-12-31'
      },
      { category: 'REDUCED', rate: new Decimal(10), validFrom: '2011-01-01', validTo: '2024-12-31' }
    ]
  },
  // -------------------------------------------------------------------------
  // Non-EU
  // -------------------------------------------------------------------------
  GB: {
    countryName: 'United Kingdom',
    isEuMember: false,
    rates: [
      { category: 'STANDARD', rate: new Decimal(20), validFrom: '2011-01-04', validTo: null },
      { category: 'REDUCED', rate: new Decimal(5), validFrom: '2011-01-04', validTo: null },
      { category: 'ZERO', rate: new Decimal(0), validFrom: '2011-01-04', validTo: null }
    ]
  },
  CH: {
    countryName: 'Switzerland',
    isEuMember: false,
    rates: [
      {
        category: 'STANDARD',
        rate: new Decimal('7.7'),
        validFrom: '2018-01-01',
        validTo: '2023-12-31'
      },
      {
        category: 'REDUCED',
        rate: new Decimal('2.5'),
        validFrom: '2018-01-01',
        validTo: '2023-12-31'
      },
      { category: 'STANDARD', rate: new Decimal('8.1'), validFrom: '2024-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal('2.6'), validFrom: '2024-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal('3.8'), validFrom: '2024-01-01', validTo: null }
    ]
  },
  NO: {
    countryName: 'Norway',
    isEuMember: false,
    rates: [
      { category: 'STANDARD', rate: new Decimal(25), validFrom: '2012-01-01', validTo: null },
      { category: 'REDUCED', rate: new Decimal(12), validFrom: '2012-01-01', validTo: null },
      { category: 'REDUCED_2', rate: new Decimal(15), validFrom: '2012-01-01', validTo: null }
    ]
  }
});

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function toDateString(date?: Temporal.PlainDate | string): string {
  if (!date) return nowPlainDate().toString();
  if (typeof date === 'string') return date.slice(0, 10);
  return date.toString();
}

function getActiveRates(rates: readonly VatRateEntry[], dateStr: string): VatRateEntry[] {
  return rates.filter(r => r.validFrom <= dateStr && (r.validTo === null || r.validTo >= dateStr));
}

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

/**
 * Get all active VAT rates for a country at a given date.
 * @param countryCode ISO 3166-1 alpha-2 (use 'EL' for Greece)
 * @param atDate defaults to today
 */
export function getVatRates(
  countryCode: string,
  atDate?: Temporal.PlainDate | string
): VatRateLookupResult | undefined {
  const code = countryCode.trim().toUpperCase();
  const country = VAT_RATES[code];
  if (!country) return undefined;

  return {
    countryCode: code,
    countryName: country.countryName,
    rates: getActiveRates(country.rates, toDateString(atDate))
  };
}

/**
 * Get the standard VAT rate for a country. When multiple STANDARD entries
 * are active (edge case), returns the one with the latest `validFrom`.
 */
export function getStandardVatRate(
  countryCode: string,
  atDate?: Temporal.PlainDate | string
): Decimal | undefined {
  const result = getVatRates(countryCode, atDate);
  if (!result) return undefined;

  const standards = result.rates
    .filter(r => r.category === VatRateCategory.STANDARD)
    .sort((a, b) => b.validFrom.localeCompare(a.validFrom));

  return standards[0]?.rate;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validate whether a VAT rate percentage is valid for a country at a given date.
 */
export function validateVatRate(
  countryCode: string,
  rate: Decimal,
  atDate?: Temporal.PlainDate | string
): ValidationResult {
  const code = countryCode.trim().toUpperCase();
  const country = VAT_RATES[code];

  if (!country) {
    return { valid: false, error: `Unknown country code: "${code}"` };
  }

  const dateStr = toDateString(atDate);
  const active = getActiveRates(country.rates, dateStr);

  if (active.length === 0) {
    return { valid: false, error: `No VAT rates found for ${code} at ${dateStr}` };
  }

  if (!active.some(r => r.rate.equals(rate))) {
    const validRates = active.map(r => `${r.rate.toFixed()}% (${r.category})`).join(', ');
    return {
      valid: false,
      error: `${rate.toFixed()}% is not a valid VAT rate for ${code} at ${dateStr}. Valid rates: ${validRates}`
    };
  }

  return { valid: true };
}

/**
 * Shorthand: returns true if the rate is valid for the country.
 */
export function isValidVatRate(
  countryCode: string,
  rate: Decimal,
  atDate?: Temporal.PlainDate | string
): boolean {
  return validateVatRate(countryCode, rate, atDate).valid;
}

// ---------------------------------------------------------------------------
// Registry queries
// ---------------------------------------------------------------------------

export function isSupportedVatCountry(countryCode: string): boolean {
  return countryCode.trim().toUpperCase() in VAT_RATES;
}

export function getSupportedVatRateCountryCodes(): string[] {
  return Object.keys(VAT_RATES);
}

export function getAllVatRates(): Record<string, CountryVatInfo> {
  const result: Record<string, CountryVatInfo> = {};
  for (const [code, country] of Object.entries(VAT_RATES)) {
    result[code] = {
      ...country,
      rates: country.rates.map(r => ({ ...r, rate: new Decimal(r.rate) }))
    };
  }
  return result;
}
